// pages/LandingPage.jsx
import { useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "../../theme/ThemeProvider";
import { useSEO } from "../../hooks/useSEO";
import Layout from "../../components/Layout";
import ErrorState from "../../components/state/ErrorState";
import EmptyState from "../../components/state/EmptyState";
import {
  fetchCategoryBrands,
  clearCategoryData,
} from "../../store/slices/categorySlice";
import { HeroSection } from "./HeroSection";
import { TopSellingProducts } from "./TopSellingProducts";
import { HowItWorks } from "./HowItWorks";
import { LandingSkeleton } from "./LandingSkeleton";

const formatName = (slug) => {
  if (!slug) return "";
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const LandingPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { categorySlug } = useParams();
  const { isDarkMode } = useTheme();

  const { selectedCity } = useSelector((state) => state.city);
  const cityId = selectedCity?.id;

  const { loading: homeLoading } = useSelector((state) => state.home);
  const {
    data: categoryData,
    loading: categoryLoading,
    error: categoryError,
  } = useSelector((state) => state.category);

  const cleanCategorySlug = categorySlug;

  const deviceName = useMemo(
    () => formatName(cleanCategorySlug),
    [cleanCategorySlug],
  );
  const cleanDeviceName = useMemo(
    () => formatName(cleanCategorySlug),
    [cleanCategorySlug],
  );

  const isLoading = homeLoading || categoryLoading;
  const hasError = categoryError;

  // Extract data from categoryData
  const category = categoryData?.data?.category || null;
  const brands = categoryData?.data?.brands || [];
  const topSelling = categoryData?.data?.top_selling || [];

  // ============= SEO =============
  const seoParams = useMemo(() => {
    if (isLoading || !category) return null;

    return {
      categorySlug: categorySlug,
      categoryName: category?.name || "",
      categoryDescription: category?.description || "",
      brandCount: brands?.length || 0,
    };
  }, [isLoading, category, categorySlug, brands]);

  useSEO(`/sell-used-online/${categorySlug}`, seoParams);

  // Fetch category brands
  useEffect(() => {
    if (cleanCategorySlug) {
      dispatch(clearCategoryData());
      dispatch(fetchCategoryBrands(cleanCategorySlug));
    }
    return () => dispatch(clearCategoryData());
  }, [dispatch, cleanCategorySlug, cityId]);

  const handleRetry = useCallback(() => {
    if (cleanCategorySlug) {
      dispatch(fetchCategoryBrands(cleanCategorySlug));
    }
  }, [dispatch, cleanCategorySlug]);

  const heroProps = useMemo(
    () => ({
      title: `Sell Used ${deviceName} for Instant Cash`,
      subtitle: `Get the best price for your ${cleanDeviceName.toLowerCase()} across India`,
    }),
    [deviceName, cleanDeviceName],
  );

  if (isLoading) {
    return (
      <Layout>
        <LandingSkeleton isDarkMode={isDarkMode} />
      </Layout>
    );
  }

  if (hasError) {
    return (
      <Layout>
        <ErrorState
          error={categoryError}
          onRetry={handleRetry}
          isDarkMode={isDarkMode}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <HeroSection
        cleanDeviceName={cleanDeviceName}
        categorySlug={cleanCategorySlug}
        getHeroTitle={() => heroProps.title}
        getHeroSubtitle={() => heroProps.subtitle}
        quickBrands={brands}
        isDarkMode={isDarkMode}
        navigate={navigate}
        categoryName={category?.name}
      />

      {topSelling.length > 0 && (
        <TopSellingProducts
          category={category?.name}
          products={topSelling}
          isDarkMode={isDarkMode}
          navigate={navigate}
        />
      )}

      <HowItWorks isDarkMode={isDarkMode} />

      {!topSelling.length && !brands.length && !isLoading && (
        <div className="container px-4 py-12 mx-auto max-w-7xl">
          <EmptyState isDarkMode={isDarkMode} />
        </div>
      )}
    </Layout>
  );
};

export default LandingPage;
